import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import config from '@/config';
import { useAppSelector, useBreakpoint, useFelaEnhanced } from '@/hooks';
import { NotificationId } from '@/modules/api/codegen';
import { useNotification } from '@/modules/notifications/hooks';
import { selectSecrets } from '@/modules/secrets/services/selectors';
import { Button, ButtonType, Icon } from '@/modules/ui';
import type { RulesExtend } from '@/styles/theme';

import { extensionStoreUrl } from '../../constants';
import banner from './assets/image.png';
import * as felaRules from './ExtensionPasskeysNotification.rules';

export interface ExtensionPasskeysNotificationProps {
    extend?: RulesExtend<typeof felaRules>;
}

export const ExtensionPasskeysNotification = ({ extend }: ExtensionPasskeysNotificationProps) => {
    const { styles } = useFelaEnhanced(felaRules, { extend });
    const { secretType } = useAppSelector(selectSecrets);
    const minMd = useBreakpoint('md');
    const { notification, isLoading, update } = useNotification(NotificationId.passkeysOnboarding);
    const [visible, setVisible] = useState(true);
    const show = !isLoading && !notification?.dismissedAt && update.status !== 'error' && visible;

    if (!extensionStoreUrl || secretType !== 'passkey' || !show || !minMd) {
        return null;
    }

    return (
        <article className={styles.container} role='banner'>
            <img src={banner} alt='Passkeys in Passwd browser extension' className={styles.banner} />
            <div>
                <h3 className={styles.title}>
                    <FormattedMessage id='extension.passkey.cta.title' />
                </h3>
                <p className={styles.text}>
                    <FormattedMessage
                        id='extension.passkey.cta.text1'
                        values={{
                            a: chunks => (
                                <a href={config.links.docsPasskeys} target='_blank' rel='noopener'>
                                    {chunks}
                                </a>
                            ),
                        }}
                    />
                </p>
                <p className={styles.text}>
                    <FormattedMessage
                        id='extension.passkey.cta.text2'
                        values={{
                            a: chunks => (
                                <a href={config.links.sitesWithPasskeys} target='_blank' rel='noopener'>
                                    {chunks}
                                </a>
                            ),
                        }}
                    />
                </p>
                <Button
                    type={ButtonType.PRIMARY}
                    size={'small'}
                    href={extensionStoreUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={styles.button}
                >
                    <FormattedMessage id='extension.passkey.cta.button' />
                </Button>
            </div>

            <Button
                className={styles.closeButton}
                type={ButtonType.TEXT}
                onClick={() => {
                    update.rejectNotification();
                    setVisible(false);
                }}
                size={'small'}
                icon={<Icon type='close' height={18} width={18} />}
            />
        </article>
    );
};
